import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { PageHero, Reveal } from "../components/Reveal";
import { GALLERY_ITEMS, COMPANY } from "../data/site";
import useSeo from "../components/Seo";

const CATS = ["All", ...new Set(GALLERY_ITEMS.map((g) => g.category))];

export default function Gallery() {
  useSeo(
    "Our Gallery — Real Operations",
    `Genuine photographs of ${COMPANY.name} operations — our trucks, tankers, yard activity and new vehicle arrivals.`
  );
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const items = filter === "All" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((g) => g.category === filter);

  useEffect(() => setActive(null), [filter]);

  useEffect(() => {
    const onKey = (e) => {
      if (active === null) return;
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((a) => (a + 1) % items.length);
      if (e.key === "ArrowLeft") setActive((a) => (a - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, items.length]);

  return (
    <>
      <PageHero
        overline="Our Gallery"
        lines={[<>Real operations.</>, <>Real <em className="italic text-gold-light">vehicles.</em></>]}
        desc={`Every photograph here belongs to ${COMPANY.name} — our trucks, tankers, yard and daily activity. This is a living record, updated as our operations grow.`}
      />

      <section className="pb-24 pt-4 sm:pb-32 sm:pt-8" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-wrap gap-2" data-testid="gallery-filters" aria-label="Gallery filters">
              {CATS.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  aria-pressed={filter === c}
                  className={`border px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 ${
                    filter === c
                      ? "border-gold bg-gold text-ink"
                      : "border-gold/25 text-foreground/70 hover:border-gold hover:text-gold-light"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
            {items.map((g, i) => (
              <Reveal key={g.image} delay={0.04 * (i % 3)} className="h-full">
                <button
                  onClick={() => setActive(i)}
                  className="group block h-full w-full border border-gold/15 bg-ink text-left transition-colors duration-500 hover:border-gold/40"
                  data-testid={`gallery-item-${i}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={g.image}
                      alt={`${g.title} — ${COMPANY.name}`}
                      className="img-spotlight aspect-[4/3] w-full object-cover"
                      loading="lazy"
                    />
                    {g.type === "video" && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold/60 bg-ink/80 backdrop-blur transition-transform duration-300 group-hover:scale-110" data-testid={`gallery-play-${i}`}>
                          <Play className="h-5 w-5 fill-gold text-gold" />
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-gold">{g.category}</p>
                    <p className="mt-2 font-heading text-lg text-foreground">{g.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{g.note}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-ink/95 p-6 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={items[active].title}
          >
            <button
              className="absolute right-6 top-6 text-foreground/70 transition-colors hover:text-gold"
              onClick={() => setActive(null)}
              aria-label="Close"
              data-testid="gallery-lightbox-close"
            >
              <X className="h-7 w-7" />
            </button>
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/60 transition-colors hover:text-gold sm:left-10"
              onClick={(e) => {
                e.stopPropagation();
                setActive((active - 1 + items.length) % items.length);
              }}
              aria-label="Previous photograph"
              data-testid="gallery-lightbox-prev"
            >
              <ChevronLeft className="h-9 w-9" />
            </button>
            <figure onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl">
              {items[active].type === "video" ? (
                <motion.video
                  key={items[active].video}
                  src={items[active].video}
                  poster={items[active].image}
                  className="max-h-[70vh] w-full border border-gold/25 object-contain"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  controls
                  autoPlay
                  playsInline
                  data-testid="gallery-lightbox-video"
                />
              ) : (
                <motion.img
                  key={items[active].image}
                  src={items[active].image}
                  alt={`${items[active].title} — ${COMPANY.name}`}
                  className="max-h-[70vh] w-full border border-gold/25 object-contain"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                />
              )}
              <figcaption className="mt-5 text-center">
                <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">{items[active].category}</span>
                <span className="mt-2 block font-heading text-xl text-foreground">{items[active].title}</span>
                <span className="mt-1 block text-sm text-muted-foreground">{items[active].note}</span>
              </figcaption>
            </figure>
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground/60 transition-colors hover:text-gold sm:right-10"
              onClick={(e) => {
                e.stopPropagation();
                setActive((active + 1) % items.length);
              }}
              aria-label="Next photograph"
              data-testid="gallery-lightbox-next"
            >
              <ChevronRight className="h-9 w-9" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
